/**
 * 业务失败响应统一构造 reject，并在归档门禁开启时 dispatch 弹窗。
 * 供 request.ts / request-light / viewer* / zooom 共用。
 * iframe 内 dispatch 后额外 postMessage 给父页，避免弹窗被裁切。
 */
import { ElMessage } from "element-plus";
import { isOssArchiveRejectError, OSS_ARCHIVE_PARENT_MESSAGE_TYPE } from "./oss-archive-error";
import { ensureOssArchiveRestoreDialog } from "./oss-archive-handler";
import { dispatchOssArchiveError, shouldHandleOssArchiveResponse } from "./oss-archive-integration";

export { OSS_ARCHIVE_PARENT_MESSAGE_TYPE };

export function notifyParentOssArchiveRestore(error: unknown): void {
	if (typeof window === "undefined") {
		return;
	}
	if (!window.parent || window.parent === window) {
		return;
	}
	window.parent.postMessage(
		{
			type: OSS_ARCHIVE_PARENT_MESSAGE_TYPE,
			error
		},
		"*"
	);
}

export function buildAndDispatchBusinessReject(
	code: unknown,
	message: unknown,
	data: unknown
): Record<string, any> {
	const rejectError: Record<string, any> = {
		code,
		message,
		data
	};
	if (data && typeof data === "object" && (data as any).error_code) {
		rejectError.error_code = (data as any).error_code;
	}
	if (shouldHandleOssArchiveResponse((data as Record<string, unknown>) || null)) {
		const opened = dispatchOssArchiveError(rejectError);
		if (opened) {
			rejectError.__ossArchiveDialogShown = true;
			notifyParentOssArchiveRestore(opened);
		}
	}
	return rejectError;
}

/**
 * axios 错误拦截器：成功拦截器 reject 的业务对象没有 response，必须原样抛出。
 * 有 response 时返回 null，由调用方继续包装 HTTP 文案（Shop 需走 i18n）。
 */
export function passthroughBusinessReject(error: any): any {
	if (!error?.response) {
		return error;
	}
	return null;
}

export function shouldSkipArchiveErrorToast(error: unknown): boolean {
	if (!error || typeof error !== "object") {
		return false;
	}
	return Boolean((error as any).__ossArchiveDialogShown);
}

/** 拦截器已弹出恢复确认窗，或 error_code 本身就是归档 */
export function isHandledOssArchiveError(error: unknown): boolean {
	return isOssArchiveRejectError(error) || shouldSkipArchiveErrorToast(error);
}

/**
 * 看图/看模型 catch 用：归档只走全局确认弹窗，不再 toast 后端原文。
 * 拦截器没弹时在这里补开（getConfig 关闸 / 无订阅）。
 * @returns true 表示已按归档处理，调用方不要再 ElMessage / 写海报原文
 */
export function notifyOssArchivePreviewError(error: unknown, fallback?: string): boolean {
	if (isOssArchiveRejectError(error) || shouldSkipArchiveErrorToast(error)) {
		ensureOssArchiveRestoreDialog(error);
		return true;
	}
	const fromError =
		error && typeof error === "object" && (error as any).message
			? String((error as any).message)
			: "";
	const msg = String(fallback || fromError || "").trim();
	if (msg) {
		ElMessage.error(msg);
	}
	return false;
}
